import { cva } from "class-variance-authority";

export type BadgeTone = "urgent" | "activity" | "info";

export interface BadgeProps
  extends Omit<
    React.HTMLAttributes<HTMLSpanElement>,
    "children" | "className" | "color"
  > {
  /** Semantic color variant. Defaults to "urgent". */
  tone?: BadgeTone;
  /**
   * Count shown in the pill. Omit to render a dot.
   * Values are capped at 3 characters.
   */
  count?: string | number;
  /** Keeps the badge mounted but hidden when false. Defaults to true. */
  visible?: boolean;
}

const badgeVariants = cva(["inline-flex", "rounded-full"], {
  variants: {
    tone: {
      urgent: "bg-error",
      activity: "bg-primary",
      info: "bg-informative",
    },
    variant: {
      dot: "size-(--spacing-6)",
      pill: "items-center justify-center min-w-(--spacing-16) h-(--spacing-16) px-(--spacing-4) py-[3px]",
    },
  },
  defaultVariants: { tone: "urgent", variant: "dot" },
});

const badgeCountVariants = cva(
  [
    "font-(family-name:--font-family-body)",
    "font-normal",
    "text-(length:--font-size-body-small)",
    "leading-[11px]",
    "text-center",
  ],
  {
    variants: {
      tone: {
        urgent: "text-error-on-error",
        activity: "text-primary-on-primary",
        info: "text-text-inverse",
      },
    },
    defaultVariants: { tone: "urgent" },
  },
);

/**
 * Status or notification indicator that renders as a dot or count badge.
 *
 * @example
 * <Badge />
 * <Badge tone="activity" count={7} />
 * <Badge tone="info" count="99+" />
 */
function Badge({
  tone = "urgent",
  count,
  visible = true,
  "aria-hidden": ariaHidden,
  "aria-label": ariaLabel,
  style,
  ...props
}: BadgeProps) {
  const isDot = count === undefined;
  const displayCount = isDot ? undefined : String(count).slice(0, 3);

  const effectiveAriaHidden: true | undefined =
    !visible || !ariaLabel || ariaHidden === true || ariaHidden === "true"
      ? true
      : undefined;

  return (
    <span
      {...props}
      role="img"
      aria-hidden={effectiveAriaHidden}
      aria-label={ariaLabel}
      className={badgeVariants({ tone, variant: isDot ? "dot" : "pill" })}
      style={!visible ? { ...style, visibility: "hidden" } : style}
    >
      {!isDot && (
        <span className={badgeCountVariants({ tone })}>{displayCount}</span>
      )}
    </span>
  );
}

export { Badge };
